import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import Header from "../components/Header";
import UseCasesSection from "../components/UseCasesSection";
import Footer from "../components/Footer";
import FAQSection from '../components/FAQSection';
import UnlockPowerSection from '../components/UnlockPowerSection';
import AppScreensSection from "../components/AppScreensSection";
import RatingsBadge from '../components/RatingsBadge';
import PricingModal from '../components/PricingModal';
import DownloadButtonsGroup from '../components/DownloadButtonsGroup';
import WaitlistModal from '../components/WaitlistModal';

export default function LandingPage() {
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [showWaitlistModal, setShowWaitlistModal] = useState(false);
  const [referrerCode, setReferrerCode] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref) setReferrerCode(ref);
  }, []);

  return (
    <div className="min-h-screen bg-[#000000] text-white font-sans flex flex-col">
      <Helmet>
        <title>AmmoPantryAI | Private Ammo &amp; Preparedness Inventory App</title>
        <meta name="description" content="Know exactly what you own. AmmoPantryAI tracks ammo, firearms, reloading supplies, medical gear, and water — all from a photo. Private by default. No cloud. No account. Free on iOS." />
        <meta name="robots" content="index, follow" />
      </Helmet>
      <Header
        onPricingClick={() => setShowPricingModal(true)}
        onJoinClick={() => setShowWaitlistModal(true)}
      />

      <PricingModal
        open={showPricingModal}
        onClose={() => setShowPricingModal(false)}
        onJoinWaitlist={() => {
          setShowPricingModal(false);
          setShowWaitlistModal(true);
        }}
      />
      <WaitlistModal
        open={showWaitlistModal}
        onClose={() => setShowWaitlistModal(false)}
        referrerCode={referrerCode}
      />

      <main className="flex-1 flex flex-col">

        {/* ── Hero ── full-viewport with hunter image background */}
        <section
          className="relative flex items-center bg-[#000000]"
          style={{
            backgroundImage: 'url("/hero-hunter.webp")',
            backgroundSize: 'cover',
            backgroundPosition: 'left 20%',
            backgroundRepeat: 'no-repeat',
            minHeight: 'calc(100vh - 76px)',
          }}
        >
          {/* Dark tint over the whole image so it reads as "faded" */}
          <div className="absolute inset-0 bg-black/55" />
          {/* Extra gradient: solid on the right for text, fade out to the left */}
          <div className="absolute inset-0 bg-gradient-to-l from-[#000000] via-[#000000]/70 to-transparent" />
          {/* Bottom fade into the next section */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#000000] via-transparent to-transparent" />

          <div className="relative z-10 w-full max-w-6xl mx-auto px-6 py-12 md:py-32">
            <div className="max-w-2xl ml-auto flex flex-col items-end text-right">
              <RatingsBadge />
              <h1 className="text-5xl md:text-7xl font-extrabold mb-6 tracking-tight leading-tight text-white">
                Track Everything.<br />
                <span className="text-[#3b82f6]">Share Nothing.</span>
              </h1>
              <p className="text-lg md:text-xl text-gray-300 mb-10 max-w-sm leading-relaxed">
                Your complete preparedness inventory — ammo, firearms, reloading supplies, medical gear, and water — tracked with a photo. You don't owe anyone a record of what you own.
              </p>
              <DownloadButtonsGroup variant="white" />
            </div>
          </div>
        </section>

        <AppScreensSection />
        <UseCasesSection />
        <UnlockPowerSection onSignUpClick={() => setShowWaitlistModal(true)} />
        <FAQSection />
      </main>

      <Footer onSignUpClick={() => setShowWaitlistModal(true)} />
    </div>
  );
}
